import { useSuperAdminOrg } from '@/shared/contexts/SuperAdminOrgContext';
import { usePersonalView } from '@/shared/contexts/PersonalViewContext';
import { useWorkspace } from '@/features/Workspaces/state/WorkspaceContext';
import { useIsSuperAdminRole } from './use-system-role';
import { useOrganization } from './api/useOrganization';

/**
 * Em que CONTEXTO a pessoa está operando.
 *
 * `user` é a visão pessoal (AB#85637): o espaço de uma pessoa dentro de uma
 * organização. Diferente dos outros três, ela não se deduz do que está
 * selecionado — é escolha explícita, guardada em `PersonalViewContext`.
 */
export type ViewScope = 'platform' | 'organization' | 'product' | 'user';

interface ViewScopeResult {
  scope: ViewScope;
  organizationId: string | null;
  productId: string | null;
  /**
   * True enquanto a identidade da organização ainda está sendo carregada
   * (perfil do usuário ou org selecionada do superadmin). Durante esse período
   * o scope é indeterminado e consumidores que decidem navegação/redirect
   * (ex: WorkspaceGuard) devem aguardar em vez de agir sobre o scope transitório.
   */
  isResolving: boolean;
}

export const useViewScope = (): ViewScopeResult => {
  const { isSuperAdmin } = useIsSuperAdminRole();
  const { isPersonalView } = usePersonalView();
  const { selectedOrganization, isLoading: isLoadingSuperAdminOrg } = useSuperAdminOrg();
  const { currentWorkspace } = useWorkspace();
  const { data: userProfile, isLoading: isLoadingProfile } = useOrganization();

  const organizationId = isSuperAdmin
    ? selectedOrganization?.id ?? null
    : userProfile?.organization_id ?? null;
  const productId = currentWorkspace?.id ?? null;

  // Antes de o orgId estar resolvido o scope cai em 'organization'/'platform'
  // mesmo que haja um produto selecionado — estado transitório no refresh.
  const isResolving = isSuperAdmin ? isLoadingSuperAdminOrg : isLoadingProfile;

  // 'platform' scope só faz sentido pra superadmin. Pra usuários comuns,
  // mid-loading do profile pode deixar organizationId=null momentaneamente —
  // sem o guard, o hook responderia 'platform' e disparava queries que
  // retornariam 403. Pra não-superadmin, mantém 'organization' mesmo sem
  // orgId resolvido (queries downstream devem checar e desabilitar).
  let scope: ViewScope;
  // A visão pessoal vence a seleção de workspace, e não a apaga: sair dela
  // devolve a pessoa exatamente ao contexto em que estava. Exige organização
  // porque o espaço pessoal é POR ORGANIZAÇÃO — a mesma pessoa em duas
  // empresas tem dois espaços, e misturá-los vazaria contexto de um cliente
  // para outro.
  if (isPersonalView && organizationId) {
    scope = 'user';
  } else if (!organizationId) {
    scope = isSuperAdmin ? 'platform' : 'organization';
  } else if (!productId) {
    scope = 'organization';
  } else {
    scope = 'product';
  }

  return { scope, organizationId, productId, isResolving };
};
